import React, { useState, useEffect } from 'react';

/**
 * Reusable modal to collect a reason/note before a booking action.
 * - When `presets` is provided, the user picks a preset reason (+ optional note).
 *   Picking "Other" requires the note.
 * - When `presets` is null, a free-text note is shown (required when `requireText`).
 */
const BookingReasonModal = ({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  confirmColor = 'red',
  presets = null,
  requireText = false,
  submitting = false,
  onConfirm,
  onClose,
}) => {
  const [selected, setSelected] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (open) {
      setSelected(presets ? presets[0] : '');
      setNote('');
    }
  }, [open, presets]);

  if (!open) return null;

  const isOther = presets && selected === 'Other';
  const buildReason = () => {
    if (!presets) return note.trim();
    if (isOther) return note.trim();
    const n = note.trim();
    return n ? `${selected} — ${n}` : selected;
  };

  const reason = buildReason();
  const minLen = presets ? (isOther ? 10 : 1) : (requireText ? 10 : 0);
  const canSubmit = !submitting && reason.length >= minLen;

  const colorMap = {
    red: 'bg-red-600 hover:bg-red-700',
    orange: 'bg-orange-500 hover:bg-orange-600',
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4" data-testid="booking-reason-modal">
      <div className="bg-white rounded-2xl w-full max-w-md flex flex-col max-h-[90vh]">
        <div className="px-5 py-4 border-b border-gray-100 shrink-0">
          <h3 className="text-lg font-bold text-gray-900">{title}</h3>
          {description && <p className="text-sm text-gray-500 mt-1">{description}</p>}
        </div>

        <div className="p-5 overflow-y-auto flex-1 space-y-3">
          {presets && (
            <div className="space-y-2" data-testid="reason-presets">
              {presets.map((p) => (
                <label
                  key={p}
                  className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition ${
                    selected === p ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                  }`}
                  data-testid={`reason-preset-${p.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`}
                >
                  <input
                    type="radio"
                    name="reason-preset"
                    value={p}
                    checked={selected === p}
                    onChange={() => setSelected(p)}
                    className="h-4 w-4 text-blue-600"
                  />
                  <span className="text-sm text-gray-800">{p}</span>
                </label>
              ))}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {presets ? (isOther ? 'Please specify' : 'Note (optional)') : 'Note'}
              {(isOther || (requireText && !presets)) && <span className="text-red-500"> *</span>}
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              placeholder={presets ? 'Add more details for the client…' : 'Add a note for the provider…'}
              data-testid="reason-note-input"
            />
          </div>
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex gap-3 shrink-0">
          <button
            onClick={onClose}
            disabled={submitting}
            className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg font-semibold hover:bg-gray-50 disabled:opacity-50"
            data-testid="reason-cancel-btn"
          >
            Back
          </button>
          <button
            onClick={() => onConfirm(reason)}
            disabled={!canSubmit}
            className={`flex-1 text-white py-2.5 rounded-lg font-semibold disabled:opacity-50 ${colorMap[confirmColor] || colorMap.red}`}
            data-testid="reason-confirm-btn"
          >
            {submitting ? 'Please wait…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingReasonModal;
